import { useDraggable } from '@dnd-kit/core';
import type { JobFileEntry } from '@jobos/shared';

import { JobFileActionsMenu, type JobFileActions } from './JobFileActionsMenu';
import { JobFileAttachmentBadge } from './JobFileAttachmentBadge';
import { JobFileThumbnail } from './JobFileThumbnail';

export type { JobFileActions };

interface JobFileCardProps extends JobFileActions {
  jobId: string;
  file: JobFileEntry;
  /** Whether this file is ticked. Only meaningful alongside `onToggleSelect`. */
  selected?: boolean;
  /** Absent when the surrounding view has no selection model — no checkbox is drawn. */
  onToggleSelect?: (id: string) => void;
}

export function JobFileCard({
  jobId,
  file,
  selected,
  onToggleSelect,
  onView,
  onEdit,
  onMove,
  onDelete,
}: JobFileCardProps) {
  // Only `listeners`, never dnd-kit's `attributes`: those put role="button" and
  // a tabIndex on this element, and this element already contains a real button
  // and a menu. A fake button wrapping real ones is worse for a screen reader
  // than having no keyboard drag — and the selection bar's Move button reaches
  // every destination a drag can.
  const { setNodeRef, listeners, isDragging } = useDraggable({ id: file.id });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      // touch-none, or the browser scrolls the page instead of letting the touch
      // sensor take the press.
      className={`relative touch-none overflow-hidden rounded-lg border border-gray-200 bg-white ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      {onToggleSelect && (
        // Its own tap target over the corner of the thumbnail, above the view
        // button so it can be reached, and swallowing pointerdown so ticking a
        // box never reads as the start of a drag.
        <label
          className="absolute left-1 top-1 z-10 flex h-8 w-8 items-center justify-center rounded-md bg-white/85 shadow-sm"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected ?? false}
            onChange={() => onToggleSelect(file.id)}
            aria-label={`Select ${file.fileName}`}
            className="h-4 w-4 accent-blue-600"
          />
        </label>
      )}

      {/* The whole image is the tap target — a thumbnail is the obvious thing to
          reach for on a phone, and it is far larger than any icon button. */}
      <button
        type="button"
        onClick={() => onView?.(file)}
        aria-label={`View ${file.fileName}`}
        className="block aspect-square w-full"
      >
        <JobFileThumbnail jobId={jobId} file={file} />
      </button>

      <div className="flex items-start gap-1 px-2 py-1.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-gray-900" title={file.fileName}>
            {file.fileName}
          </p>
          <JobFileAttachmentBadge jobId={jobId} file={file} />
        </div>

        <JobFileActionsMenu
          jobId={jobId}
          file={file}
          onView={onView}
          onEdit={onEdit}
          onMove={onMove}
          onDelete={onDelete}
          className="h-8 w-8 shrink-0"
        />
      </div>
    </div>
  );
}
